import { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useUploadStore, type UploadJob, type UploadStage } from '@/stores/upload.store';
import { colors, radius } from '@/theme/tokens';

const stageLabel: Record<UploadStage, string> = {
  queued: 'Queued',
  uploading: 'Uploading',
  creating: 'Verifying',
  completed: 'Completed',
  error: 'Failed',
  cancelled: 'Cancelled',
};

const isActive = (s: UploadStage) =>
  s === 'queued' || s === 'uploading' || s === 'creating';

const formatSpeed = (bps: number) => {
  const mbps = bps / (1024 * 1024);
  if (mbps >= 1) return `${mbps.toFixed(1)} MB/s`;
  return `${Math.max(1, Math.round(bps / 1024))} KB/s`;
};

const formatEta = (seconds: number) => {
  if (seconds < 60) return `~${Math.max(1, Math.round(seconds))}s left`;
  if (seconds < 3600) return `~${Math.round(seconds / 60)}m left`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `~${h}h ${m}m left`;
};

const StageIcon = ({ stage }: { stage: UploadStage }) => {
  if (stage === 'completed') return <CheckCircleIcon fontSize="small" sx={{ color: colors.success.main }} />;
  if (stage === 'error') return <ErrorIcon fontSize="small" sx={{ color: colors.error.main }} />;
  if (stage === 'cancelled') return <CancelIcon fontSize="small" sx={{ color: colors.text.tertiary }} />;
  return <UploadIcon fontSize="small" sx={{ color: colors.primary.main }} />;
};

const JobRow = ({ job }: { job: UploadJob }) => {
  const cancel = useUploadStore((s) => s.cancel);
  const dismiss = useUploadStore((s) => s.dismiss);
  const indeterminate = job.stage === 'creating';
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StageIcon stage={job.stage} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap title={job.uploadFileName}>
            {job.uploadFileName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {job.catalogEntryLabel} · v{job.version}
          </Typography>
        </Box>
        {isActive(job.stage) ? (
          <Tooltip title="Cancel">
            <IconButton size="small" onClick={() => cancel(job.id)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Dismiss">
            <IconButton size="small" onClick={() => dismiss(job.id)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {isActive(job.stage) && (
        <Box sx={{ mt: 0.75 }}>
          <LinearProgress
            variant={indeterminate ? 'indeterminate' : 'determinate'}
            value={indeterminate ? undefined : job.progress}
            sx={{ height: 4, borderRadius: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {stageLabel[job.stage]}
            {job.stage === 'uploading' && ` · ${job.progress}%`}
            {job.stage === 'uploading' && job.speedBps != null && ` · ${formatSpeed(job.speedBps)}`}
            {job.stage === 'uploading' && job.etaSeconds != null && ` · ${formatEta(job.etaSeconds)}`}
          </Typography>
        </Box>
      )}
      {job.stage === 'error' && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {job.error || 'Upload failed'}
        </Typography>
      )}
      {(job.stage === 'completed' || job.stage === 'cancelled') && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {stageLabel[job.stage]}
        </Typography>
      )}
    </Box>
  );
};

export function UploadManagerTray() {
  const jobs = useUploadStore((s) => s.jobs);
  const clearAll = useUploadStore((s) => s.clearAll);
  const [collapsed, setCollapsed] = useState(false);

  const activeCount = useMemo(() => jobs.filter((j) => isActive(j.stage)).length, [jobs]);

  if (jobs.length === 0) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 28,
        right: 16,
        width: 340,
        maxHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${radius.lg}px`,
        overflow: 'hidden',
        zIndex: 1300,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          bgcolor: colors.background.darkPaper,
          color: 'common.white',
          cursor: 'pointer',
        }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Uploads {activeCount > 0 ? `(${activeCount} active)` : `(${jobs.length})`}
        </Typography>
        <IconButton size="small" sx={{ color: 'common.white' }} onClick={(e) => { e.stopPropagation(); setCollapsed((c) => !c); }}>
          {collapsed ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Tooltip
          title={
            activeCount > 0 ? 'Cancel in-progress uploads first' : 'Clear all'
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={activeCount > 0}
              sx={{
                color: 'common.white',
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
              }}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {!collapsed && (
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default UploadManagerTray;
